// src/LinkedInCompanyPage.js
import React, { useEffect, useState } from 'react';
import {
  Container, Tabs, Tab, Spinner, Row, Col, Card, Button
} from 'react-bootstrap';
import { DollarSign, TrendingUp, Users } from 'react-feather';
import DataTable from 'react-data-table-component';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import BankSearchBar from './BankSearchBar';
import { fetchFinancials } from './api';
import useSessionState from './hooks/useSessionState';
import './LinkedInCompanyPage.css';

const fmt = n =>
  n == null ? 'N/A'
    : n >= 1e9 ? (n / 1e9).toFixed(2) + ' B'
    : n >= 1e6 ? (n / 1e6).toFixed(2) + ' M'
    : n.toLocaleString('en-US');

export default function LinkedInCompanyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedBank, setSelectedBank] = useSessionState('selBank', null);
  const [tab, setTab]                   = useSessionState('bankTab', 'about');

  const [jobs, setJobs]                 = useState([]);
  const [loadingJobs, setLoadingJobs]   = useState(false);
  const [bankDetails, setBankDetails]   = useState(null);
  const [loadingDet, setLoadingDet]     = useState(false);
  const [numempSeries, setNumempSeries] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(false);

  // 1) İş ilanları
  useEffect(() => {
    if (!selectedBank) return;
    setLoadingJobs(true);
    fetch(`/api/jobs?bank=${encodeURIComponent(selectedBank.value)}`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setJobs(d))
      .catch(() => setJobs([]))
      .finally(() => setLoadingJobs(false));
  }, [selectedBank]);

  // 2) Bank details
  useEffect(() => {
    if (!selectedBank || jobs.length === 0) {
      setBankDetails(null);
      return;
    }
    setLoadingDet(true);
    fetch(`/api/bank-details?rssdId=${jobs[0]._source.IDRSSD}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setBankDetails(d))
      .catch(() => setBankDetails(null))
      .finally(() => setLoadingDet(false));
  }, [selectedBank, jobs]);

  // 3) FDIC NUMEMP serisi
  useEffect(() => {
    if (!bankDetails) {
      setNumempSeries([]);
      return;
    }
    setLoadingSeries(true);
    fetchFinancials(bankDetails.IDRSSD, 'NUMEMP', 100)
      .then(s => setNumempSeries(s.map(pt => [pt.date.getTime(), pt.value])))
      .catch(() => setNumempSeries([]))
      .finally(() => setLoadingSeries(false));
  }, [bankDetails]);

  const chartOpts = {
    title: { text: `Number of Employees — ${selectedBank?.label || ''}` },
    xAxis: { type: 'datetime' },
    yAxis: { title: { text: 'Employees' } },
    tooltip: {
      useHTML: true,
      formatter() {
        const d = new Date(this.x);
        return `<b>${d.getFullYear()} Q${Math.floor(d.getMonth()/3)+1}</b><br/># of Employees: ${this.y}`;
      }
    },
    series: [{ name: 'NUMEMP', data: numempSeries }]
  };

  const cards = bankDetails ? [
    bankDetails.ASSET   && { t:'Total Assets',   i:<DollarSign size={28}/>,   v:'$'+fmt(bankDetails.ASSET*1000) },
    bankDetails.NETINCQ && { t:'Net Income (Q)', i:<TrendingUp size={28}/>,    v:'$'+fmt(bankDetails.NETINCQ*1000) },
    bankDetails.NUMEMP  && { t:'Employees',      i:<Users size={28}/>,         v:fmt(bankDetails.NUMEMP) }
  ].filter(Boolean) : [];

  const cols = [
    {
      name: 'Title',
      selector: r=>r._source.title,
      sortable: true,
      cell: r=>(
        <a href={r._source.url} target="_blank" rel="noopener noreferrer">
          {r._source.title}
        </a>
      )
    },
    { name:'State',     selector:r=>r._source.state, sortable:true },
    { name:'City',      selector:r=>r._source.city,  sortable:true },
    { name:'Post Date', selector:r=>r._source.post_date||r._source.scrapedate, sortable:true }
  ];

  return (
    <Container className="company-page-container mt-5">
      {/* HEADER */}
      <div className="company-header text-center mb-4">
        <h1>Banks</h1>
        <p className="company-subtitle">Explore our extensive bank database</p>
      </div>

      {/* SEARCH BAR */}
      <div className="mb-4">
        <BankSearchBar
          onSelect={opt => {
            setSelectedBank(opt);
            setJobs([]); setBankDetails(null); setNumempSeries([]);
          }}
          initialValue={selectedBank}
        />
      </div>

      {selectedBank ? (
        <Tabs id="bank-tabs" activeKey={tab} onSelect={k=>setTab(k)} className="mb-3 custom-tabs">
          {/* ABOUT */}
          <Tab eventKey="about" title="About">
            {loadingDet
              ? <Spinner animation="border"/>
              : bankDetails
                ? <Row>
                    <Col lg={7}>
                      <Row className="mb-3">
                        {cards.map(c=>(
                          <Col md={4} key={c.t}>
                            <Card className="fancy-card text-white mb-2">
                              <Card.Body>
                                <div className="d-flex align-items-center mb-2">
                                  {c.i}<span className="ms-2">{c.t}</span>
                                </div>
                                <h4 className="mb-0">{c.v}</h4>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                      <h4>{bankDetails.NAME}</h4>
                      <p>{bankDetails.SUMMARY}</p>
                    </Col>
                    <Col lg={5}>
                      {bankDetails.SIMS_LAT && bankDetails.SIMS_LONG && (
                        <>
                          <div className="map-wrapper mb-2">
                            <iframe
                              title="map" width="100%" height="220" frameBorder="0"
                              src={`https://www.openstreetmap.org/export/embed.html?bbox=${bankDetails.SIMS_LONG-0.05}%2C${bankDetails.SIMS_LAT-0.05}%2C${bankDetails.SIMS_LONG+0.05}%2C${bankDetails.SIMS_LAT+0.05}&layer=mapnik&marker=${bankDetails.SIMS_LAT}%2C${bankDetails.SIMS_LONG}`}
                            />
                          </div>
                          <p><strong>Location:</strong> {bankDetails.CITY}, {bankDetails.STNAME} {bankDetails.ZIP}</p>
                          <p><strong>Website:</strong>{' '}
                            <a href={`https://${bankDetails.WEBADDR}`} target="_blank" rel="noopener noreferrer">
                              {bankDetails.WEBADDR}
                            </a>
                          </p>
                        </>
                      )}
                    </Col>
                  </Row>
                : <p>No details available for this bank.</p>
            }
          </Tab>

          {/* JOBS */}
          <Tab eventKey="jobs" title="Jobs">
            <h4 className="mb-3">Job Openings at {selectedBank.label}</h4>
            {loadingJobs
              ? <Spinner animation="border"/>
              : <>


                  {/* Tablo ya da kısıtlı önizleme */}
                  {!user
                    ? (
                      <div className="limited-preview mb-4">
                        <p>
                          This bank has a total of <strong>{jobs.length}</strong>  listings; you’re currently seeing only the first <strong>2</strong>. Please sign up to access the rest.


                        </p>
                        <Row className="mb-3">
                          {jobs.slice(0,2).map(j=>(
                            <Col md={6} key={j._id} className="mb-3">
                              <Card className="h-100">
                                <Card.Body>
                                  <Card.Title>{j._source.title}</Card.Title>
                                  <Card.Text>
                                    <strong>Location:</strong> {j._source.city}, {j._source.state}
                                  </Card.Text>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                        <Button onClick={()=>navigate('/signup')} className="me-2">Sign Up</Button>
                        <Button onClick={()=>navigate('/login')}>Log In</Button>
                      </div>
                    )
                    : (
                      <DataTable
                        title="All Job Listings"
                        columns={cols}
                        data={jobs}
                        pagination
                        responsive
                      />
                    )
                  }
                </>
            }
          </Tab>

          {/* DETAILS */}
          <Tab eventKey="details" title="Details">
            <h4 className="mb-3">Employee History for {selectedBank.label}</h4>
            {loadingSeries
              ? <Spinner animation="border"/>
              : <HighchartsReact highcharts={Highcharts} options={chartOpts}/>
            }
          </Tab>
        </Tabs>
      ) : (
        <p className="text-center text-muted">Start typing a bank name and select one.</p>
      )}
    </Container>
  );
}
